import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, Row, Col, Badge, Button, Table, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faLayerGroup, 
  faHome, 
  faUser, 
  faFileContract, 
  faWrench, 
  faCog, 
  faExclamationTriangle,
  faClock,
  faCheckCircle,
  faTimesCircle,
  faPhone,
  faEye,
  faEdit
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../contexts/AuthContext';
import { propertyService } from '../../services/propertyService';
import { contractService } from '../../services/contractService';
import { userService } from '../../services/userService';
import { maintenanceService } from '../../services/maintenanceService';
import { tenantService } from '../../services/tenantService';

const ManagerDashboard = () => {
  const { userCompany, logout } = useAuth();
  const navigate = useNavigate();
  const [propertyGroups, setPropertyGroups] = useState([]);
  const [propertyUnits, setPropertyUnits] = useState([]);
  const [maintenanceRequests, setMaintenanceRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
          const [companyLogo, setCompanyLogo] = useState('');
        const [logoEnabled, setLogoEnabled] = useState(false);
  const [tenants, setTenants] = useState([]);
  const [contracts, setContracts] = useState([]);
  


  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };



  useEffect(() => {
    const fetchData = async () => {
      console.log('=== MANAGER DASHBOARD FETCH DATA ===');
      console.log('userCompany value:', userCompany);
      console.log('userCompany type:', typeof userCompany);
      
      if (!userCompany) {
        console.log('userCompany is not available, skipping data fetch');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        console.log('Fetching data for company:', userCompany);
        
        const [propertyGroupsData, propertyUnitsData, tenantsData, contractsData, maintenanceData, settingsData] = await Promise.all([
          propertyService.getPropertyGroupsByCompany(userCompany),
          propertyService.getUnitsByCompany(userCompany),
          tenantService.getTenantsByCompany(userCompany),
          contractService.getContractsByCompany(userCompany),
          maintenanceService.getMaintenanceRequestsByCompany(userCompany),
          contractService.getBrokerageSettings(userCompany)
        ]);

        console.log('Data fetched successfully:');
        console.log('Property groups:', propertyGroupsData.length);
        console.log('Property units:', propertyUnitsData.length);
        console.log('Tenants:', tenantsData.length);
        console.log('Contracts:', contractsData.length);
        console.log('Maintenance requests:', maintenanceData.length);

        setPropertyGroups(propertyGroupsData);
        setPropertyUnits(propertyUnitsData);
        setTenants(tenantsData);
        setContracts(contractsData);
        setMaintenanceRequests(maintenanceData);
        setLogoEnabled(settingsData.logoEnabled || false);
        setCompanyLogo((settingsData.logoEnabled && settingsData.logoUrl) ? settingsData.logoUrl : '');
      } catch (error) {
        console.error('Dashboard data fetch error:', error);
        setError(`Failed to load dashboard data: ${error.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [userCompany]);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Stats Cards */}
      <Row className="mb-4">
        <Col lg={3} md={6} className="mb-3">
          <Card className="border-0 shadow-sm">
            <Card.Body>
              <div className="d-flex align-items-center">
                <div className="flex-shrink-0">
                  <div className="bg-primary bg-opacity-10 p-3 rounded">
                    <FontAwesomeIcon icon={faLayerGroup} className="text-primary" size="lg" />
                  </div>
                </div>
                <div className="flex-grow-1 ms-3">
                  <h4 className="mb-1">{propertyGroups.length}</h4>
                  <p className="text-muted mb-0">Property Groups</p>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={3} md={6} className="mb-3">
          <Card className="border-0 shadow-sm">
            <Card.Body>
              <div className="d-flex align-items-center">
                <div className="flex-shrink-0">
                  <div className="bg-success bg-opacity-10 p-3 rounded">
                    <FontAwesomeIcon icon={faHome} className="text-success" size="lg" />
                  </div>
                </div>
                <div className="flex-grow-1 ms-3">
                  <h4 className="mb-1">{propertyUnits.length}</h4>
                  <p className="text-muted mb-0">Total Units</p>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={3} md={6} className="mb-3">
          <Card className="border-0 shadow-sm">
            <Card.Body>
              <div className="d-flex align-items-center">
                <div className="flex-shrink-0">
                  <div className="bg-warning bg-opacity-10 p-3 rounded">
                    <FontAwesomeIcon icon={faWrench} className="text-warning" size="lg" />
                  </div>
                </div>
                <div className="flex-grow-1 ms-3">
                  <h4 className="mb-1">{maintenanceRequests.filter(r => r.status === 'pending').length}</h4>
                  <p className="text-muted mb-0">Pending Maint.</p>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={3} md={6} className="mb-3">
          <Card className="border-0 shadow-sm">
            <Card.Body>
              <div className="d-flex align-items-center">
                <div className="flex-shrink-0">
                  <div className="bg-secondary bg-opacity-10 p-3 rounded">
                    <FontAwesomeIcon icon={faHome} className="text-secondary" size="lg" />
                  </div>
                </div>
                <div className="flex-grow-1 ms-3">
                  <h4 className="mb-1">{propertyUnits.filter(u => u.status === 'available').length}</h4>
                  <p className="text-muted mb-0">Unoccupied Units</p>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Maintenance Section */}
      <Card className="border-0 shadow-sm mb-4">
        <Card.Header className="bg-transparent border-0">
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">
              <FontAwesomeIcon icon={faWrench} className="text-warning me-2" />
              Maintenance Requests
            </h5>
            <Button as={Link} to="/manager/maintenance" variant="outline-warning" size="sm">
              View All
            </Button>
          </div>
        </Card.Header>
        <Card.Body>
          {/* Maintenance Stats */}
          <Row className="mb-3">
            <Col md={3} className="mb-2">
              <div className="d-flex align-items-center p-2 bg-warning bg-opacity-10 rounded">
                <FontAwesomeIcon icon={faExclamationTriangle} className="text-warning me-2" />
                <div>
                  <div className="fw-bold">{maintenanceRequests.filter(r => r.status === 'pending').length}</div>
                  <small className="text-muted">Pending</small>
                </div>
              </div>
            </Col>
            <Col md={3} className="mb-2">
              <div className="d-flex align-items-center p-2 bg-info bg-opacity-10 rounded">
                <FontAwesomeIcon icon={faClock} className="text-info me-2" />
                <div>
                  <div className="fw-bold">{maintenanceRequests.filter(r => r.status === 'in_progress').length}</div>
                  <small className="text-muted">In Progress</small>
                </div>
              </div>
            </Col>
            <Col md={3} className="mb-2">
              <div className="d-flex align-items-center p-2 bg-success bg-opacity-10 rounded">
                <FontAwesomeIcon icon={faCheckCircle} className="text-success me-2" />
                <div>
                  <div className="fw-bold">{maintenanceRequests.filter(r => r.status === 'completed').length}</div>
                  <small className="text-muted">Completed</small>
                </div>
              </div>
            </Col>
            <Col md={3} className="mb-2">
              <div className="d-flex align-items-center p-2 bg-secondary bg-opacity-10 rounded">
                <FontAwesomeIcon icon={faTimesCircle} className="text-secondary me-2" />
                <div>
                  <div className="fw-bold">{maintenanceRequests.filter(r => r.status === 'cancelled').length}</div>
                  <small className="text-muted">Cancelled</small>
                </div>
              </div>
            </Col>
          </Row>

          {/* Recent Maintenance Requests */}
          <Table responsive hover>
            <thead>
              <tr>
                <th>Request ID</th>
                <th>Tenant</th>
                <th>Title</th>
                <th>Type</th>
                <th>Priority</th>
                <th>Status</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {maintenanceRequests.slice(0, 5).map((request) => (
                <tr key={request.id}>
                  <td>
                    <strong className="text-primary">{request.requestId}</strong>
                  </td>
                  <td>
                    <div>
                      <strong>{request.tenantName}</strong>
                      {request.unitNumber && (
                        <div className="text-muted small">Unit: {request.unitNumber}</div>
                      )}
                    </div>
                  </td>
                  <td>
                    <div>
                      <strong>{request.title}</strong>
                      {request.contactPhone && (
                        <div className="text-muted small">
                          <FontAwesomeIcon icon={faPhone} className="me-1" />
                          {request.contactPhone}
                        </div>
                      )}
                    </div>
                  </td>
                  <td>
                    <Badge bg="secondary">
                      {request.type === 'plumbing' ? 'Plumbing' :
                       request.type === 'electrical' ? 'Electrical' :
                       request.type === 'hvac' ? 'HVAC' :
                       request.type === 'appliance' ? 'Appliance' :
                       request.type === 'structural' ? 'Structural' :
                       request.type === 'pest_control' ? 'Pest Control' :
                       request.type === 'cleaning' ? 'Cleaning' :
                       request.type === 'landscaping' ? 'Landscaping' :
                       request.type === 'security' ? 'Security' :
                       request.type}
                    </Badge>
                  </td>
                  <td>
                    <Badge bg={
                      request.priority === 'low' ? 'success' :
                      request.priority === 'medium' ? 'warning' :
                      request.priority === 'high' ? 'danger' :
                      request.priority === 'urgent' ? 'danger' : 'secondary'
                    }>
                      {request.priority}
                    </Badge>
                  </td>
                  <td>
                    <Badge bg={
                      request.status === 'pending' ? 'warning' :
                      request.status === 'in_progress' ? 'info' :
                      request.status === 'completed' ? 'success' :
                      request.status === 'cancelled' ? 'secondary' : 'secondary'
                    }>
                      {request.status === 'pending' ? 'Pending' :
                       request.status === 'in_progress' ? 'In Progress' :
                       request.status === 'completed' ? 'Completed' :
                       request.status === 'cancelled' ? 'Cancelled' : request.status}
                    </Badge>
                  </td>
                  <td>
                    <div className="text-muted small">
                      {request.createdAt?.toDate?.()?.toLocaleDateString() || 'N/A'}
                    </div>
                  </td>
                  <td>
                    <div className="d-flex gap-1">
                      <Button as={Link} to={`/manager/maintenance/${request.id}`} variant="outline-primary" size="sm">
                        <FontAwesomeIcon icon={faEye} />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          {maintenanceRequests.length === 0 && (
            <div className="text-center text-muted py-4">
              <FontAwesomeIcon icon={faWrench} size="2x" className="mb-3" />
              <p>No maintenance requests found</p>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Property Groups */}
      <Card className="border-0 shadow-sm mb-4">
        <Card.Header className="bg-transparent border-0">
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">Property Groups</h5>
            <Button as={Link} to="/manager/property-groups" variant="outline-primary" size="sm">
              View All
            </Button>
          </div>
        </Card.Header>
        <Card.Body>
          <Table responsive hover>
            <thead>
              <tr>
                <th>Group Name</th>
                <th>Type</th>
                <th>Location</th>
                <th>Units Count</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {propertyGroups.slice(0, 5).map((group) => {
                const unitsInGroup = propertyUnits.filter(unit => unit.groupId === group.id).length;
                return (
                  <tr key={group.id}>
                    <td>
                      <div className="d-flex align-items-center">
                        <FontAwesomeIcon icon={faLayerGroup} className="text-primary me-2" />
                        {group.name}
                      </div>
                    </td>
                    <td>
                      <Badge bg="warning">
                        {group.type}
                      </Badge>
                    </td>
                    <td>{group.location}</td>
                    <td>
                      <Badge bg="secondary">{unitsInGroup}</Badge>
                    </td>
                    <td>
                      <Badge bg={group.status === 'active' ? 'success' : 'secondary'}>
                        {group.status}
                      </Badge>
                    </td>
                    <td>
                      <div className="d-flex gap-1">
                        <Button as={Link} to={`/manager/property-groups/${group.id}`} variant="outline-primary" size="sm">
                          <FontAwesomeIcon icon={faEye} />
                        </Button>
                        <Button as={Link} to={`/manager/property-groups/${group.id}/edit`} variant="outline-warning" size="sm">
                          <FontAwesomeIcon icon={faEdit} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      {/* Recent Property Units */}
      <Card className="border-0 shadow-sm">
        <Card.Header className="bg-transparent border-0">
          <div className="d-flex justify-content-between align-items-center">
            <h5 className="mb-0">Recent Property Units</h5>

          </div>
        </Card.Header>
        <Card.Body>
          <Table responsive hover>
            <thead>
              <tr>
                <th>Unit Number</th>
                <th>Group</th>
                <th>Type</th>
                <th>Size</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {propertyUnits.slice(0, 5).map((unit) => {
                const group = propertyGroups.find(g => g.id === unit.groupId);
                return (
                  <tr key={unit.id}>
                    <td>
                      <div className="d-flex align-items-center">
                        <FontAwesomeIcon icon={faHome} className="text-success me-2" />
                        {unit.unitNumber}
                      </div>
                    </td>
                    <td>{group?.name || 'N/A'}</td>
                    <td>
                      <Badge bg="info">{unit.unitType}</Badge>
                    </td>
                    <td>{unit.area ? `${unit.area} sq ft` : 'N/A'}</td>
                    <td>
                      <Badge bg={
                        unit.status === 'available' ? 'success' : 
                        unit.status === 'occupied' ? 'warning' : 
                        unit.status === 'maintenance' ? 'danger' : 
                        unit.status === 'reserved' ? 'info' : 'secondary'
                      }>
                        {unit.status}
                      </Badge>
                    </td>
                    <td>
                      <div className="d-flex gap-1">
                        <Button as={Link} to={`/manager/units/${unit.id}`} variant="outline-primary" size="sm">
                          <FontAwesomeIcon icon={faEye} />
                        </Button>
                        <Button as={Link} to={`/manager/units/${unit.id}/edit`} variant="outline-warning" size="sm">
                          <FontAwesomeIcon icon={faEdit} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </Card.Body>
      </Card>


    </div>
  );
};

export default ManagerDashboard; 